import type { ReactElement } from "react";
import { MousePointerClickIcon, XIcon } from "lucide-react";

import type { BrowserCommentTarget } from "@cmmd-forge/contracts";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import {
  COMPOSER_INLINE_CHIP_CLASS_NAME,
  COMPOSER_INLINE_CHIP_DISMISS_BUTTON_CLASS_NAME,
  COMPOSER_INLINE_CHIP_ICON_CLASS_NAME,
  COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME,
} from "../composerInlineChip";
import { cn } from "~/lib/utils";
import { formatComposerElementContextLabel } from "./composerElementContext.logic";

/**
 * The browser element a comment is anchored to, shown in the composer.
 *
 * Ported from t3code's `ComposerPendingElementContexts`. Forge could already
 * *select* an element in the browser preview — `BrowserCommentDraft.target` —
 * but the selection was only visible inside the browser panel, so once you
 * moved to the composer there was nothing saying a target was still armed, and
 * no way to drop it without going back. This is that missing half: state what
 * is attached, and let go of it from where you are typing.
 */
export function ComposerPendingElementContexts(props: {
  readonly target: BrowserCommentTarget | null;
  readonly onRemove: () => void;
  readonly className?: string | undefined;
}): ReactElement | null {
  if (!props.target) return null;

  const { label, detail } = formatComposerElementContextLabel(props.target);

  return (
    <div className={cn("flex flex-wrap gap-1.5", props.className)} data-composer-element-contexts>
      <Tooltip>
        <TooltipTrigger
          render={
            <span className={cn(COMPOSER_INLINE_CHIP_CLASS_NAME, "pr-1")}>
              <MousePointerClickIcon className={COMPOSER_INLINE_CHIP_ICON_CLASS_NAME} />
              <span className={COMPOSER_INLINE_CHIP_LABEL_CLASS_NAME}>{label}</span>
              <button
                type="button"
                aria-label="Remove browser element context"
                data-composer-element-context-remove
                className={COMPOSER_INLINE_CHIP_DISMISS_BUTTON_CLASS_NAME}
                onClick={props.onRemove}
              >
                <XIcon className="size-3" />
              </button>
            </span>
          }
        />
        <TooltipPopup side="top">{detail ?? label}</TooltipPopup>
      </Tooltip>
    </div>
  );
}
